import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  ReactText,
} from 'react';
import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'framer-motion';
import flattenChildren from 'react-keyed-flatten-children';
import { animations, mixins } from '../../core/styles/theme/default';
import { isSetsEqual } from '../../core/utils/Utils';

const StyledAccordionItem = styled.div``;

const StyledAccordionItemTrigger = styled.button`
  ${mixins.buttonReset};
  display: flex;
  align-items: center;
`;

const StyledAccordionItemContent = styled(motion.div)``;

const AccordionContext = createContext({});
const AccordionItemContext = createContext({});

const BaseAccordion = ({
  allowMultipleExpanded,
  allowItemCollapse,
  preExpanded,
  onChange,
  children,
}: BaseAccordionProps) => {
  const [expandedItems, setExpandedItems] = useState<Set<ReactText>>(
    new Set(preExpanded),
  );

  return (
    <AccordionContext.Provider
      value={{
        allowMultipleExpanded,
        allowItemCollapse,
        expandedItems,
        setExpandedItems,
        onChange,
      }}
    >
      {flattenChildren(children).map((child: any, index: number) => {
        const hasCustomUuid = Object.hasOwnProperty.call(child.props, 'uuid');

        /**
         * pass uuid from child prop or auto-generate uuid using the index - starting by 1
         */
        return React.cloneElement(child, {
          uuid: hasCustomUuid ? child.props.uuid : index + 1,
        });
      })}
    </AccordionContext.Provider>
  );
};

const BaseAccordionItem = ({
  children,
  uuid,
  ...rest
}: BaseAccordionItemProps) => {
  const { expandedItems }: any = useContext(AccordionContext);
  const [isExpanded, setIsExpanded] = useState<boolean>(
    !!expandedItems.has(uuid),
  );

  useEffect(() => {
    setIsExpanded(!!expandedItems.has(uuid));
  }, [uuid, expandedItems]);

  return (
    <AccordionItemContext.Provider value={{ uuid, isExpanded, setIsExpanded }}>
      <StyledAccordionItem {...rest}>{children}</StyledAccordionItem>
    </AccordionItemContext.Provider>
  );
};

const BaseAccordionItemTrigger = ({
  children,
  ...rest
}: BaseAccordionItemTriggerProps) => {
  const {
    allowMultipleExpanded,
    allowItemCollapse,
    expandedItems,
    setExpandedItems,
    onChange,
  }: any = useContext(AccordionContext);
  const { uuid, isExpanded }: any = useContext(AccordionItemContext);

  const toggleAccordionItem = () => {
    /**
     * create active uuid List
     * if allowMultipleExpanded is set populate with expandedItems (this enables multiples opened AccordionItems)
     * if allowItemCollapse (but not allowMultipleExpanded) is set only populate the current uuid
     * otherwise with an empty array (only one expanded AccordionItem allowed)
     */
    let uuidList: Set<ReactText>;
    if (allowMultipleExpanded) {
      uuidList = new Set(expandedItems);
    } else if (allowItemCollapse) {
      const values = expandedItems.has(uuid) ? [uuid] : [];
      uuidList = new Set(values);
    } else {
      uuidList = new Set([]);
    }

    /**
     * check if allowItemCollapse is set and the uuid is already in the set
     * if the condition is met - remove the uuid from the set (this will collapse an opened AccordionItem)
     * otherwise add it (this will expand an AccordionItem)
     */
    if (allowItemCollapse && uuidList.has(uuid)) {
      uuidList.delete(uuid);
    } else {
      uuidList.add(uuid);
    }

    /**
     * if expandedItems and uuidList are not equal update state
     * this prevents unneeded re-renders when clicking on the AccordionTrigger
     */
    if (!isSetsEqual(expandedItems, uuidList)) {
      setExpandedItems(uuidList);
    }

    /** fire callback function - pass last clicked uuid and array of expandedItems */
    onChange && onChange(uuid, Array.from(uuidList));
  };

  return (
    <StyledAccordionItemTrigger
      data-uuid={uuid}
      onClick={toggleAccordionItem}
      aria-expanded={isExpanded}
      {...rest}
    >
      {flattenChildren(children).map((child: any) => {
        // check if child type is a React Component and pass isExpanded to it
        if (typeof child.type === 'function') {
          return React.cloneElement(child, { isExpanded });
        } else {
          return child;
        }
      })}
    </StyledAccordionItemTrigger>
  );
};

const BaseAccordionItemContent = ({
  children,
  ...rest
}: BaseAccordionItemContentProps) => {
  const { isExpanded }: any = useContext(AccordionItemContext);

  return (
    // @ts-ignore
    <AnimatePresence>
      <StyledAccordionItemContent
        initial="collapsed"
        animate={isExpanded ? 'expanded' : 'collapsed'}
        exit="collapsed"
        variants={{
          expanded: {
            opacity: 1,
            height: 'auto',
            overflow: 'visible',
            transition: { duration: animations.durationDefault },
          },
          collapsed: {
            opacity: 0,
            height: 0,
            overflow: 'hidden',
            transition: { duration: animations.durationFast },
          },
        }}
        {...rest}
      >
        {children}
      </StyledAccordionItemContent>
    </AnimatePresence>
  );
};

export type BaseAccordionProps = {
  /** Children of the Accordion */
  children: ReactNode;
  /** Allow multiple expanded AccordionItems */
  allowMultipleExpanded?: boolean;
  /** Allow collapse an expanded AccordionItem */
  allowItemCollapse?: boolean;
  /** List of preExpanded AccordionItems */
  preExpanded?: Array<ReactText>;
  /** Callback Function that will be called when an AccordionItem changes - will return the uuid of the clicked item and the currently expanded items */
  onChange?: (uuid: ReactText, expandedItems: Array<ReactText>) => void;
};

export type BaseAccordionItemProps = {
  /** Children of the AccordionItem */
  children: ReactNode;
  /** uuid of the AccordionItem */
  uuid?: ReactText;
};

export type BaseAccordionItemTriggerProps = {
  /** Children of the AccordionTrigger */
  children: ReactNode;
};

export type BaseAccordionItemContentProps = {
  /** Children of the AccordionTrigger */
  children: ReactNode;
  className?: string;
  /** Animation Variants */
  variants?: {
    expanded: {
      [key: string]: any;
    };
    collapsed: {
      [key: string]: any;
    };
  };
};

export {
  BaseAccordion,
  BaseAccordionItem,
  BaseAccordionItemTrigger,
  BaseAccordionItemContent,
};
